import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';

import { makeFirstLetterUppercase } from '@/lib/util';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { SizeState } from '@/views/StrapView/components';

export const useStrapSizes = (
  sizesSkus: string[],
  strapId: string,
  onlyAvailableSizes: boolean,
  setSizeState: Dispatch<SetStateAction<SizeState>>,
  sizeState: SizeState,
  strapSizeDatas?: Record<string, any>,
) => {
  const {
    query: { languageCode },
  } = useRegionProvider();

  useEffect(() => {
    if (!strapSizeDatas) return;

    const items = strapSizeDatas.variant_group
      ?.filter((sku: string) => {
        const variant = strapSizeDatas.variants[sku];
        if (!variant?.strap_length) return false;

        if (!onlyAvailableSizes) return true;
        return variant?.call_to_action === 'ORDER';
      })
      .map((sku: string) => {
        const variant = strapSizeDatas.variants[sku];
        return {
          value: variant?.variant_id,
          label: makeFirstLetterUppercase(
            variant?.strap_length?.[languageCode]?.toLowerCase() || '',
          ),
          data: {
            sku,
            id: variant?.variant_id,
            // Use English values (EN_GB preferred, fallback to EN_US) to ensure compatibility with sizeMap inside useWristSize.
            size:
              variant?.strap_length?.['EN_GB'] ||
              variant?.strap_length?.['EN_US'] ||
              '',
          },
        };
      });

    const currentStrapItem = items?.find(
      (el: { value: string }) => el.value === strapId,
    );

    setSizeState((prevState) => {
      const shouldUpdateSelectedItem =
        currentStrapItem &&
        (!sizeState.sizesSelectedItem ||
          sizeState.sizesSelectedItem.value !== currentStrapItem.value);

      return {
        ...prevState,
        sizesSelectedItem: shouldUpdateSelectedItem
          ? currentStrapItem
          : prevState.sizesSelectedItem,
        selectItems: items,
        isLoadingOtherSize: false,
      };
    });
  }, [strapId, onlyAvailableSizes, strapSizeDatas, languageCode]);
};
